import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Loader2,
  AlertTriangle,
  Search,
  Calendar,
  X,
  ArrowLeft,
  Filter,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useGetAuditLogQuery, useGetAuditLogMetaQuery } from '../../api/apiSlice';
import Pagination from '../../components/common/Pagination';

// AdminAuditLogPage
// ─────────────────
// Полная история audit-событий с фильтрами и пагинацией.
//
// Фильтры:
//   • action — селект (семейство ИЛИ конкретный action из meta)
//   • search — по email/имени актора
//   • from/to — диапазон createdAt
//   • targetType + targetId — точечный поиск по конкретной сущности
//
// Принципы UI:
//   • Серверная пагинация — таблица не пытается тащить миллион записей
//   • Бэйджи action разноцветные по семейству (унифицированы с дашбордом)
//   • Раскрываемые JSON metadata — компактный вид по умолчанию,
//     полный — по клику (иначе одна аномальная запись растягивает таблицу)

const PAGE_SIZE = 50;

const AUDIT_TONE = {
  account: { dot: 'bg-violet-500', text: 'text-violet-700 dark:text-violet-300', bg: 'bg-violet-50 dark:bg-violet-900/20' },
  login:   { dot: 'bg-sky-500',    text: 'text-sky-700 dark:text-sky-300',       bg: 'bg-sky-50 dark:bg-sky-900/20' },
  oauth:   { dot: 'bg-blue-500',   text: 'text-blue-700 dark:text-blue-300',     bg: 'bg-blue-50 dark:bg-blue-900/20' },
  password:{ dot: 'bg-amber-500',  text: 'text-amber-700 dark:text-amber-300',   bg: 'bg-amber-50 dark:bg-amber-900/20' },
  order:   { dot: 'bg-emerald-500',text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
  payout:  { dot: 'bg-teal-500',   text: 'text-teal-700 dark:text-teal-300',     bg: 'bg-teal-50 dark:bg-teal-900/20' },
  course:  { dot: 'bg-rose-500',   text: 'text-rose-700 dark:text-rose-300',     bg: 'bg-rose-50 dark:bg-rose-900/20' },
};
const DEFAULT_TONE = { dot: 'bg-slate-400', text: 'text-slate-700 dark:text-slate-300', bg: 'bg-slate-50 dark:bg-slate-900/20' };
const toneFor = (action) => AUDIT_TONE[String(action).split('.')[0]] || DEFAULT_TONE;

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

const AdminAuditLogPage = () => {
  // ─── Фильтры ───────────────────────────────
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [targetType, setTargetType] = useState('');
  const [targetId, setTargetId] = useState('');
  const [page, setPage] = useState(1);

  // Debounce поиска — не дёргаем бэк на каждый клавишный код.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  // При смене любого фильтра сбрасываем на первую страницу.
  useEffect(() => { setPage(1); }, [action, from, to, targetType, targetId]);

  const { data: meta } = useGetAuditLogMetaQuery();
  const { data, isFetching, error } = useGetAuditLogQuery({
    page, limit: PAGE_SIZE,
    action: action || undefined,
    search: search || undefined,
    from: from || undefined,
    to: to || undefined,
    targetType: targetType || undefined,
    targetId: targetId || undefined,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const pageCount = data?.pageCount || 1;

  // Список семейств + точечных action'ов из meta — для селекта.
  // Семейства первыми (Family · *), потом конкретные действия (count рядом).
  const actionOptions = useMemo(() => {
    if (!meta) return [];
    const families = (meta.families || []).map((f) => ({ value: f, label: `${f} · все`, group: 'families' }));
    const actions = (meta.actions || []).map((a) => ({ value: a.action, label: `${a.action} (${a.count})`, group: 'actions' }));
    return [...families, ...actions];
  }, [meta]);

  const hasFilters = !!(search || action || from || to || targetType || targetId);
  const resetAll = () => {
    setSearch('');
    setSearchInput('');
    setAction('');
    setFrom('');
    setTo('');
    setTargetType('');
    setTargetId('');
    setPage(1);
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link
            to="/dashboard/admin"
            className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition mb-2"
          >
            <ArrowLeft className="w-3 h-3" /> Дашборд
          </Link>
          <h1 className="flex items-center gap-2 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-md shadow-violet-500/30">
              <Activity className="w-5 h-5" />
            </span>
            Аудит-лог
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            История всех чувствительных действий: входы, оплаты, выплаты, изменение паролей,
            модерация курсов, удаления аккаунтов.
          </p>
        </div>
        {total > 0 && (
          <div className="text-right">
            <div className="text-xs text-slate-500 dark:text-slate-400">Всего событий</div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{total.toLocaleString('ru-RU')}</div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/60 dark:ring-slate-800 p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* Search */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 inline-block">
              Поиск (email / имя актора)
            </span>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="например, ivanov@..."
                className="w-full rounded-xl bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-700 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </label>

          {/* Action */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 inline-block">
              Тип действия
            </span>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="w-full rounded-xl bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Все действия</option>
              <optgroup label="Семейства">
                {actionOptions.filter((o) => o.group === 'families').map((o) => (
                  <option key={`f-${o.value}`} value={o.value}>{o.label}</option>
                ))}
              </optgroup>
              <optgroup label="Конкретные">
                {actionOptions.filter((o) => o.group === 'actions').map((o) => (
                  <option key={`a-${o.value}`} value={o.value}>{o.label}</option>
                ))}
              </optgroup>
            </select>
          </label>

          {/* Date from */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 inline-block">
              С даты
            </span>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                max={to || undefined}
                className="w-full rounded-xl bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-700 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </label>

          {/* Date to */}
          <label className="block">
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 inline-block">
              По дату
            </span>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                min={from || undefined}
                className="w-full rounded-xl bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-700 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </label>
        </div>

        {/* Точечный target — опциональный второй ряд */}
        <details className="mt-3 group">
          <summary className="text-xs font-semibold text-slate-500 dark:text-slate-400 cursor-pointer inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400">
            <Filter className="w-3 h-3" />
            Точечный поиск по сущности
            <ChevronDown className="w-3 h-3 group-open:hidden" />
            <ChevronUp className="w-3 h-3 hidden group-open:inline" />
          </summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 inline-block">
                Тип сущности (User / Order / Course / Payout)
              </span>
              <input
                type="text"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value)}
                placeholder="например, Order"
                className="w-full rounded-xl bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1 inline-block">
                ID сущности
              </span>
              <input
                type="text"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                placeholder="cmplcbiec00004cf13qba9ety"
                className="w-full rounded-xl bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-700 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </label>
          </div>
        </details>

        {/* Active filters chips + reset */}
        {hasFilters && (
          <div className="mt-4 flex items-center flex-wrap gap-2">
            {search && <FilterChip label={`поиск: ${search}`} onClear={() => { setSearch(''); setSearchInput(''); }} />}
            {action && <FilterChip label={`action: ${action}`} onClear={() => setAction('')} />}
            {from && <FilterChip label={`с ${from}`} onClear={() => setFrom('')} />}
            {to && <FilterChip label={`по ${to}`} onClear={() => setTo('')} />}
            {targetType && <FilterChip label={`type: ${targetType}`} onClear={() => setTargetType('')} />}
            {targetId && <FilterChip label={`id: ${targetId.slice(0, 12)}…`} onClear={() => setTargetId('')} />}
            <button
              type="button"
              onClick={resetAll}
              className="text-xs text-slate-500 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 underline transition"
            >
              сбросить все
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {error ? (
        <div className="rounded-2xl bg-red-50 dark:bg-red-900/20 ring-1 ring-red-200 dark:ring-red-800/40 p-5 text-red-700 dark:text-red-300 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">Не удалось загрузить лог</p>
            <p className="text-xs mt-1">{error?.data?.message || error?.error || 'Серверная ошибка'}</p>
          </div>
        </div>
      ) : isFetching && items.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/60 dark:ring-slate-800 p-16 text-center">
          <Activity className="w-10 h-10 mx-auto mb-3 text-slate-400" />
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            Ничего не найдено
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Попробуйте ослабить фильтры или сбросить их
          </p>
        </div>
      ) : (
        <div className={`rounded-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/60 dark:ring-slate-800 overflow-hidden ${isFetching ? 'opacity-60' : ''}`}>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((e) => <AuditEntry key={e.id} entry={e} />)}
          </ul>
        </div>
      )}

      {pageCount > 1 && (
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
            Стр. {page} из {pageCount} · показано {items.length} из {total.toLocaleString('ru-RU')}
          </div>
          <Pagination
            page={page}
            pageCount={pageCount}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </div>
      )}
    </div>
  );
};

const FilterChip = ({ label, onClear }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-[11px] font-semibold px-2.5 py-1">
    {label}
    <button
      type="button"
      onClick={onClear}
      className="hover:text-red-600 dark:hover:text-red-400"
      title="Убрать фильтр"
    >
      <X className="w-3 h-3" />
    </button>
  </span>
);

const AuditEntry = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const tone = toneFor(entry.action);
  const actorLabel = entry.actor?.deleted
    ? `${entry.actor.id.slice(0, 8)}… (удалён)`
    : entry.actor?.name || entry.actor?.email || 'System';
  const actorSub = entry.actor?.deleted
    ? null
    : entry.actor?.email && entry.actor?.name
    ? entry.actor.email
    : null;
  const metaEntries = entry.metadata && Object.keys(entry.metadata).length > 0
    ? Object.entries(entry.metadata)
    : [];

  return (
    <li className="px-4 sm:px-5 py-3">
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${tone.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold ${tone.bg} ${tone.text}`}>
              {entry.action}
            </span>
            <span className="text-sm font-semibold text-slate-900 dark:text-white truncate">
              {actorLabel}
            </span>
            {actorSub && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                {actorSub}
              </span>
            )}
            {entry.targetType && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                → {entry.targetType}
                {entry.targetId ? `: ${String(entry.targetId).slice(0, 16)}…` : ''}
              </span>
            )}
          </div>

          {/* Compact metadata + expand */}
          {metaEntries.length > 0 && !open && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 truncate font-mono">
              {metaEntries.slice(0, 3).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')}
              {metaEntries.length > 3 && ` · +${metaEntries.length - 3} ещё`}
            </p>
          )}
          {open && (
            <pre className="mt-2 rounded-lg bg-slate-50 dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 p-3 text-[11px] text-slate-700 dark:text-slate-300 overflow-x-auto whitespace-pre-wrap break-words font-mono">
{JSON.stringify({
  metadata: entry.metadata,
  actorIp: entry.actorIp,
  actorUserAgent: entry.actorUserAgent,
  fullTargetId: entry.targetId,
}, null, 2)}
            </pre>
          )}
        </div>

        <div className="text-right shrink-0 flex flex-col items-end gap-1">
          <span className="text-[11px] text-slate-400 tabular-nums whitespace-nowrap">
            {fmtTime(entry.createdAt)}
          </span>
          {(metaEntries.length > 0 || entry.actorIp || entry.actorUserAgent) && (
            <button
              type="button"
              onClick={() => setOpen((s) => !s)}
              className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              {open ? 'Свернуть' : 'Детали'}
            </button>
          )}
        </div>
      </div>
    </li>
  );
};

export default AdminAuditLogPage;
